import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { AvatarChip } from '@/components/AvatarChip';
import { container } from '@/services/container';
import { useNetworkStore } from '@/store/network';
import { useFeedStore } from '@/store/feed';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { unseenCircleCount } from '@/features/circle/circleSeen';
import { MeshLinkError } from '@/lib/result';
import {
  OFFICIAL_ADDRESS,
  OFFICIAL_NAME,
  OFFICIAL_ANNOUNCEMENTS,
  markOfficialFollowed,
} from '@/utils/official';

/** did:dpb:0x… → 0x… 地址（创作者关注/订阅的目标）。 */
function addressFromDid(did: string): string {
  const s = String(did ?? '').trim();
  return s.startsWith('did:dpb:') ? s.slice('did:dpb:'.length).toLowerCase() : '';
}

/**
 * 发现页：在线用户目录 + 按 PeerID 添加好友 + 创作者（关注/订阅挖矿）。
 *
 * 在线列表由 `RelayService` 心跳（15s）通过 `useNetworkStore.online` 实时更新；
 * 「刷新在线」按钮先清空列表、3s 后回填刷新前的快照 —— 给用户一个可观测的刷新过程，
 * 不改变心跳维护的真实数据。
 */
export function DiscoverPage(): JSX.Element {
  const navigate = useNavigate();
  const online = useNetworkStore((s) => s.online);
  const lastRefresh = useNetworkStore((s) => s.lastOnlineRefreshAt);
  const relayStatus = useNetworkStore((s) => s.relay.status);
  const posts = useFeedStore((s) => s.posts);
  const contacts = useChatStore((s) => s.contacts);
  const following = useSocialStore((s) => s.following);
  const selfDid = useIdentityStore((s) => s.did);

  const [peerIdInput, setPeerIdInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  // 创作者聚合：从广场帖作者去重，提取链地址（关注/订阅目标）
  const creators = useMemo(() => {
    const map = new Map<string, { did: string; address: string; name: string; posts: number }>();
    for (const p of posts) {
      const did = typeof p.authorDid === 'string' ? p.authorDid : '';
      const address = addressFromDid(did);
      if (!address) continue;
      const prev = map.get(address);
      const name = typeof p.authorName === 'string' && p.authorName ? p.authorName : address.slice(0, 10);
      if (prev) {
        prev.posts += 1;
      } else {
        map.set(address, { did, address, name, posts: 1 });
      }
    }
    return [...map.values()].sort((a, b) => b.posts - a.posts).slice(0, 20);
  }, [posts]);

  const doAdd = async (id: string): Promise<void> => {
    const trimmed = id.trim();
    if (!trimmed) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setAdding(true);
    try {
      await container.chat.addContact(trimmed, '');
      setPeerIdInput('');
      useUiStore.getState().pushToast('success', '已发送好友请求');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setAdding(false);
    }
  };

  /** 朋友圈未读（v3.21）：好友动态数中晚于已读时间戳的数量。 */
  const circleUnseen = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    if (selfDid) set.add(selfDid.toLowerCase());
    return unseenCircleCount(posts, set);
  }, [posts, contacts, following, selfDid]);

  const refresh = (): void => {
    if (refreshing) return;
    setRefreshing(true);
    // 先清空 online（列表位置会短暂显示 EmptyState）→ 3s 后把刷新前快照重新推回，
    // 用可观测的变化模拟「重新拉取在线列表」的过程；真实列表仍由 15s 心跳维护。
    const snapshot = useNetworkStore.getState().online;
    useNetworkStore.getState().setOnline([]);
    window.setTimeout(() => {
      useNetworkStore.getState().setOnline(snapshot);
      setRefreshing(false);
    }, 3_000);
  };

  /** 关注 / 订阅创作者（社交动作上链 → 行为挖矿奖励）。 */
  const engage = async (action: 'follow' | 'subscribe', address: string, name: string): Promise<void> => {
    const key = `${action}:${address}`;
    if (busyAction) return;
    const self = container.identity.address;
    if (!self) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setBusyAction(key);
    try {
      await container.chain.socialAct({
        action,
        from: self as `0x${string}`,
        target: address as `0x${string}`,
        signature: container.identity.signObject({ action, from: self, target: address, ts: Date.now() }),
      });
      if (address === OFFICIAL_ADDRESS) markOfficialFollowed();
      useUiStore.getState().pushToast('success', action === 'follow' ? `已关注 ${name}` : `已订阅 ${name}（上链）`);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* 朋友圈（v3.20）：好友动态流入口 */}
      <section className="card p-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-xl" aria-hidden>
            📸
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text">朋友圈</p>
            <p className="mt-0.5 text-[11px] text-dim">仅好友与关注对象的动态 · 分享此刻（发文即挖矿）</p>
          </div>
          <Button size="sm" onClick={() => navigate('/circle')}>
            去看看{circleUnseen > 0 ? ` · ${circleUnseen}` : ''}
          </Button>
        </div>
      </section>

      {/* 官方号（v3.16 内容冷启动）：官方公告 + 一键关注（上链 + 联动新手任务） */}
      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">📢 {OFFICIAL_NAME}</h2>
          <Button
            variant="ghost"
            size="sm"
            loading={busyAction === `follow:${OFFICIAL_ADDRESS}`}
            onClick={() => void engage('follow', OFFICIAL_ADDRESS, OFFICIAL_NAME)}
          >
            关注官方号（上链）
          </Button>
        </div>
        <ul className="space-y-2.5">
          {OFFICIAL_ANNOUNCEMENTS.map((a) => (
            <li key={a.title} className="rounded-btn bg-panel-2 px-3 py-2.5">
              <p className="text-xs font-medium text-text">
                {a.icon} {a.title}
                <span className="ml-2 text-[11px] text-dim">{a.ts}</span>
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-dim">{a.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">按 PeerID 添加好友</h2>
        <div className="flex gap-2">
          <Input
            value={peerIdInput}
            onChange={(e) => setPeerIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doAdd(peerIdInput);
            }}
            placeholder="粘贴对方 PeerID"
          />
          <Button size="sm" loading={adding} onClick={() => void doAdd(peerIdInput)}>
            添加
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">
            在线用户 <span className="text-[11px] text-dim">({online.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            {refreshing ? <span className="text-[11px] text-ok">刷新中…</span> : null}
            <Button variant="ghost" size="sm" loading={refreshing} onClick={refresh}>
              刷新在线
            </Button>
          </div>
        </div>

        <p className="mb-3 text-[11px] text-dim">
          中继：{relayStatus}
          {lastRefresh ? ` · 上次刷新 ${new Date(lastRefresh).toLocaleTimeString()}` : ''}
        </p>

        {online.length === 0 ? (
          <EmptyState title="附近暂无在线用户" description="稍等片刻或邀请好友上线" />
        ) : (
          <ul className="space-y-2">
            {online.map((p) => (
              <li
                key={p.peerId}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate text-text">{p.name || '匿名用户'}</p>
                  <p className="mono truncate text-[11px] text-dim">{p.peerId}</p>
                </div>
                <Button variant="ghost" size="sm" loading={adding} onClick={() => void doAdd(p.peerId)}>
                  加好友
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">
          创作者 <span className="text-[11px] text-dim">(关注/订阅即挖矿 · {creators.length})</span>
        </h2>
        {creators.length === 0 ? (
          <EmptyState title="暂无创作者" description="广场帖子会聚合出创作者列表" />
        ) : (
          <ul className="space-y-2">
            {creators.map((c) => (
              <li
                key={c.address}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate text-text">{c.name}</p>
                  <p className="mono truncate text-[11px] text-dim">
                    {c.address.slice(0, 10)}… · {c.posts} 帖
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <AvatarChip did={`did:dpb:${c.address}`} name={c.name} size="sm" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/u/${encodeURIComponent(`did:dpb:${c.address}`)}`)}
                  >
                    主页
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busyAction === `follow:${c.address}`}
                    onClick={() => void engage('follow', c.address, c.name)}
                  >
                    关注
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busyAction === `subscribe:${c.address}`}
                    onClick={() => void engage('subscribe', c.address, c.name)}
                  >
                    订阅
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default DiscoverPage;
